import React, { useState, useEffect, useRef } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { createPortal } from 'react-dom';
import { mcpService, type ManagedMcpIntegration, type McpToolDefinition } from '../../services/mcpService';
import { Button } from '../ui/Button';
import { SelectField, TextArea, TextField } from '../ui/Field';
import { Overlay } from '../ui/Overlay';
import { Surface } from '../ui/Surface';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface FieldSourceActionRef {
  capabilityCode: string;
  responsePath?: string;
}

export interface KeyValue {
  key: string;
  value: string;
  meta?: BodyTemplateMeta;
}

export interface ActionConfig {
  id?: string;
  capabilityCode?: string;
  bindingType?: 'api_endpoint' | 'mcp_tool';
  mcpServerId?: string;
  mcpToolName?: string;
  mcpInputTemplate?: Record<string, any>;
  name: string;
  description?: string;
  url: string;
  method: HttpMethod;
  headers: KeyValue[];
  queryParams: KeyValue[];

  // 🔥 NEW
  bodyTemplateWithMetaData?: any;

  // 🧓 OLD (unchanged)
  bodyTemplate?: KeyValue[];
}

const buildBodyTemplateFromCapability = (capabilityDetails: any): KeyValue[] => {
  const params = capabilityDetails?.config?.action?.parameters;
  if (!Array.isArray(params)) return [];

  return params.map((p: any) => {
    let valueType: FieldValueType = 'text';
    let format: string | undefined;

    if (p.format?.includes('YYYY')) {
      valueType = 'date';
      format = 'YYYY-MM-DD';
    } else if (p.format?.includes('HH')) {
      valueType = 'time';
      format = 'HH:mm';
    } else if (p.type === 'number' || p.type === 'integer') {
      valueType = 'number';
    }

    return {
      key: p.name,
      value: `{{${p.name}}}`,
      meta: {
        valueType,
        format,
      },
    };
  });
};

export type FieldValueType =
  | 'text'
  | 'number'
  | 'date'
  | 'time'
  | 'datetime'
  | 'daterange'
  | 'timerange'
  | 'datetimerange'
  | 'single-select'
  | 'multi-select'
  | 'duration'
  | 'enum';

export interface SelectOption {
  label: string;
  value: string;
}

export interface BodyTemplateMeta {
  source?: 'entity' | 'static';
  format?: string;          // Expected API format
  valueType?: FieldValueType;
  options?: Array<string | SelectOption>;       // For select types
  sourceActions?: FieldSourceActionRef[];
  validationType?: string;  // Maps to mcp-fields-validation FieldValueType
  /** UI-only: format select is showing the custom-input row. Not persisted. */
  formatCustom?: boolean;
}

export interface KeyValue {
  key: string;
  value: string;
  meta?: BodyTemplateMeta;
}

const normalizeSelectOptions = (
  options: Array<string | SelectOption> | undefined,
  keepIncomplete: boolean = false
): SelectOption[] => {
  if (!Array.isArray(options)) return [];
  return options
    .map((opt) => {
      if (typeof opt === 'string') {
        if (keepIncomplete) {
          return { label: opt, value: opt };
        }
        const value = opt.trim();
        return value ? { label: value, value } : null;
      }

      if (keepIncomplete) {
        const label = String(opt?.label ?? '');
        const value = String(opt?.value ?? '');
        return { label, value };
      }
      const label = String(opt?.label ?? '').trim();
      const value = String(opt?.value ?? '').trim();
      if (!label || !value) return null;
      return { label, value };
    })
    .filter(Boolean) as SelectOption[];
};

const VALUE_TYPE_OPTIONS: { label: string; value: FieldValueType }[] = [
  { label: 'Text', value: 'text' },
  { label: 'Number', value: 'number' },
  { label: 'Date', value: 'date' },
  { label: 'Time', value: 'time' },
  { label: 'Date & Time', value: 'datetime' },
  { label: 'Date Range', value: 'daterange' },
  { label: 'Time Range', value: 'timerange' },
  { label: 'DateTime Range', value: 'datetimerange' },
  { label: 'Single Select', value: 'single-select' },
  { label: 'Multi Select', value: 'multi-select' },
  { label: 'Duration (e.g. 1 hour, 30 min)', value: 'duration' },
];

export const DATE_FORMATS = [
  { label: 'YYYY-MM-DD (2026-01-29)', value: 'YYYY-MM-DD' },
  { label: 'DD-MM-YYYY (29-01-2026)', value: 'DD-MM-YYYY' },
  { label: 'MM-DD-YYYY (01-29-2026)', value: 'MM-DD-YYYY' },
  { label: 'MM/DD/YYYY (01/29/2026)', value: 'MM/DD/YYYY' },
  { label: 'DD/MM/YYYY (29/01/2026)', value: 'DD/MM/YYYY' },
  { label: 'YYYY/MM/DD (2026/01/29)', value: 'YYYY/MM/DD' },
  { label: 'DD MMM YYYY (29 Jan 2026)', value: 'DD MMM YYYY' },
  { label: 'MMM DD, YYYY (Jan 29, 2026)', value: 'MMM DD, YYYY' },
  { label: 'MMMM DD, YYYY (January 29, 2026)', value: 'MMMM DD, YYYY' },
  { label: 'DD MMMM YYYY (29 January 2026)', value: 'DD MMMM YYYY' },
  { label: 'ddd, DD MMM YYYY (Thu, 29 Jan 2026)', value: 'ddd, DD MMM YYYY' },
  { label: 'dddd, DD MMMM YYYY (Thursday, 29 January 2026)', value: 'dddd, DD MMMM YYYY' },
];

export const TIME_FORMATS = [
  { label: 'HH:mm (14:30)', value: 'HH:mm' },
  { label: 'HH:mm:ss (14:30:45)', value: 'HH:mm:ss' },
  { label: 'hh:mm A (02:30 PM)', value: 'hh:mm A' },
  { label: 'hh:mm:ss A (02:30:45 PM)', value: 'hh:mm:ss A' },
  { label: 'HHmm (1430)', value: 'HHmm' },
];

export const DATETIME_FORMATS = [
  { label: 'YYYY-MM-DD HH:mm', value: 'YYYY-MM-DD HH:mm' },
  { label: 'YYYY-MM-DD HH:mm:ss', value: 'YYYY-MM-DD HH:mm:ss' },
  { label: 'DD-MM-YYYY HH:mm', value: 'DD-MM-YYYY HH:mm' },
  { label: 'DD/MM/YYYY HH:mm', value: 'DD/MM/YYYY HH:mm' },
  { label: 'MM/DD/YYYY hh:mm A', value: 'MM/DD/YYYY hh:mm A' },
  { label: 'DD MMM YYYY hh:mm A (29 Jan 2026 02:30 PM)', value: 'DD MMM YYYY hh:mm A' },
  { label: 'MMM DD, YYYY hh:mm A (Jan 29, 2026 02:30 PM)', value: 'MMM DD, YYYY hh:mm A' },
  { label: 'YYYY-MM-DDTHH:mm:ss (ISO Local)', value: 'YYYY-MM-DDTHH:mm:ss' },
  { label: 'YYYY-MM-DDTHH:mm:ssZ (ISO UTC)', value: 'YYYY-MM-DDTHH:mm:ss[Z]' },
];

export const DURATION_FORMATS = [
  { label: 'Seconds (3600)', value: 'seconds' },
  { label: 'Minutes (60)', value: 'minutes' },
  { label: 'Hours (24)', value: 'hours' },
  { label: 'HH:mm (01:00)', value: 'HH:mm' },
  // { label: 'ISO 8601 (PT1H30M)', value: 'iso8601' },
];


export interface AvailableSourceAction {
  capabilityCode: string;
  name: string;
}


const recordToKeyValues = (obj: Record<string, any> | undefined | null): KeyValue[] => {
  if (!obj || typeof obj !== 'object') return [];
  return Object.entries(obj).map(([key, value]) => ({
    key,
    value: typeof value === 'string' ? value : JSON.stringify(value),
  }));
};

interface ActionConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialConfig?: Partial<ActionConfig>;
  onSubmit: (config: ActionConfig) => void;
  onTest?: (config: ActionConfig) => Promise<void> | void;
  capabilityCode?: string;
  capabilityDetails: any;
  agentInstanceId?: string;
  /** Other configured actions on this agent (excluding the action being edited). */
  availableSourceActions?: AvailableSourceAction[];
}

export const ActionConfigModal: React.FC<ActionConfigModalProps> = ({
  isOpen,
  onClose,
  initialConfig,
  onSubmit,
  onTest,
  capabilityCode,
  capabilityDetails,
  agentInstanceId,
  availableSourceActions = [],
}) => {
  const [bindingType, setBindingType] = useState<'api_endpoint' | 'mcp_tool'>('api_endpoint');
  const [mcpServerId, setMcpServerId] = useState<string>('');
  const [mcpToolName, setMcpToolName] = useState<string>('');
  const [mcpInputRows, setMcpInputRows] = useState<KeyValue[]>([]);
  const dragIndexRef = useRef<number | null>(null);
  const [mcpIntegrations, setMcpIntegrations] = useState<ManagedMcpIntegration[]>([]);
  const [mcpTools, setMcpTools] = useState<McpToolDefinition[]>([]);
  const [loadingMcpIntegrations, setLoadingMcpIntegrations] = useState(false);
  const [loadingMcpTools, setLoadingMcpTools] = useState(false);
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [url, setUrl] = useState<string>('');
  const [method, setMethod] = useState<HttpMethod>('POST');
  const [headers, setHeaders] = useState<KeyValue[]>([]);
  const [queryParams, setQueryParams] = useState<KeyValue[]>([]);
  const [bodyTemplateWithMetaData, setBodyTemplateWithMetaData] =
    useState<KeyValue[]>([]);
  const [responsePath, setResponsePath] = useState<string>('');

  const [authMethod, setAuthMethod] = useState<HttpMethod>('POST');
  const [authUrl, setAuthUrl] = useState('');
  const [authHeaders, setAuthHeaders] = useState<KeyValue[]>([]);
  const [authBody, setAuthBody] = useState<KeyValue[]>([]);
  const [authTokenResponsePath, setAuthTokenResponsePath] = useState('');
  const [authTemplateVariable, setAuthTemplateVariable] = useState('');

  const [authAttachScheme, setAuthAttachScheme] = useState<
    'none' | 'bearer' | 'basic'
  >('none');
  const [basicAuthUsername, setBasicAuthUsername] = useState('');
  const [basicAuthPassword, setBasicAuthPassword] = useState('');

  const [bodyTemplate, setBodyTemplate] =
    useState<KeyValue[]>([]);

  const [testing, setTesting] = useState(false);

  const isRagLikeCapability = /faq|knowledge-base|kbqa/i.test(capabilityCode || '');
  const defaultApiUrl = isRagLikeCapability ? '/pdf-search' : '';

  useEffect(() => {
    console.log("capabilityDetails==>", JSON.stringify(capabilityDetails))
  }, [capabilityDetails])

  useEffect(() => {
    if (!isOpen) return;

    if (initialConfig) {
      setBindingType(initialConfig.bindingType || 'api_endpoint');
      setMcpServerId(initialConfig.mcpServerId || '');
      setMcpToolName(initialConfig.mcpToolName || '');
      {
        const tpl = (initialConfig.mcpInputTemplate || {}) as Record<string, any>;
        const order: string[] = Array.isArray(tpl.__order) ? tpl.__order : [];
        const types: Record<string, string> = (tpl.__types && typeof tpl.__types === 'object') ? tpl.__types : {};
        const options: Record<string, Array<{label: string; value: string}>> = (tpl.__options && typeof tpl.__options === 'object') ? tpl.__options : {};
        const formats: Record<string, string> = (tpl.__formats && typeof tpl.__formats === 'object') ? tpl.__formats : {};
        const sourceActions: Record<string, FieldSourceActionRef[]> =
          (tpl.__sourceActions && typeof tpl.__sourceActions === 'object') ? tpl.__sourceActions : {};
        const rows = Object.entries(tpl)
          .filter(([k]) => !k.startsWith('__'))
          .map(([key, value]) => ({
            key,
            value: typeof value === 'string' ? value : JSON.stringify(value),
            meta: {
              validationType: types[key] || '',
              options: options[key] || [],
              format: formats[key] || '',
              sourceActions: sourceActions[key] || [],
            },
          }));
        if (order.length) {
          rows.sort((a, b) => {
            const ai = order.indexOf(a.key);
            const bi = order.indexOf(b.key);
            return (ai === -1 ? 9999 : ai) - (bi === -1 ? 9999 : bi);
          });
        }
        setMcpInputRows(rows);
      }

      setName(initialConfig.name || '');
      setDescription(initialConfig.description || '');
      setUrl(initialConfig.url || defaultApiUrl);
      setMethod(initialConfig.method || 'POST');
      setHeaders(initialConfig.headers || []);
      setQueryParams(
        (initialConfig.queryParams as KeyValue[]) || []
      );

      // 🔥 NEW default-capability flow
      setBodyTemplateWithMetaData(
        initialConfig.bodyTemplateWithMetaData?.body?.length
          ? initialConfig.bodyTemplateWithMetaData?.body
          : buildBodyTemplateFromCapability(capabilityDetails)
      );
      setResponsePath(initialConfig.bodyTemplateWithMetaData?.responsePath || '');

      const af = (initialConfig.bodyTemplateWithMetaData as any)?.authFetch;
      setAuthMethod((af?.method as HttpMethod) || 'POST');
      setAuthUrl(af?.url || '');
      setAuthHeaders(recordToKeyValues(af?.headers));
      setAuthBody(recordToKeyValues(af?.body));
      setAuthTokenResponsePath(af?.tokenResponsePath || '');
      setAuthTemplateVariable(af?.templateVariable || '');

      const aa = (initialConfig.bodyTemplateWithMetaData as any)?.authAttach;
      const s = aa?.scheme;
      setAuthAttachScheme(
        s === 'bearer' || s === 'basic' ? s : 'none'
      );
      setBasicAuthUsername(aa?.basicUsername || '');
      setBasicAuthPassword(aa?.basicPassword || '');

      // Attach any saved query param meta back onto queryParams state
      const savedQueryMeta: KeyValue[] =
        (initialConfig.bodyTemplateWithMetaData as any)?.query || [];
      if (savedQueryMeta?.length) {
        setQueryParams(prev =>
          prev.map(row => {
            const metaMatch = savedQueryMeta.find(m => m.key === row.key);
            return metaMatch ? { ...row, meta: metaMatch.meta } : row;
          })
        );
      }

      // 🧓 OLD flow untouched
      setBodyTemplate(initialConfig.bodyTemplate || []);
    } else {
      setBindingType('api_endpoint');
      setMcpServerId('');
      setMcpToolName('');
      setMcpInputRows([]);
      setName('');
      setDescription('');
      setUrl(defaultApiUrl);
      setMethod('POST');
      setHeaders([]);
      setQueryParams([]);

      setBodyTemplateWithMetaData(
        buildBodyTemplateFromCapability(capabilityDetails)
      );
      setResponsePath('');

      setAuthMethod('POST');
      setAuthUrl('');
      setAuthHeaders([]);
      setAuthBody([]);
      setAuthTokenResponsePath('');
      setAuthTemplateVariable('');

      setAuthAttachScheme('none');
      setBasicAuthUsername('');
      setBasicAuthPassword('');

      setBodyTemplate([]);
    }
  }, [isOpen, initialConfig, capabilityDetails]);

  useEffect(() => {
    if (!isOpen || !agentInstanceId) return;
    const load = async () => {
      try {
        setLoadingMcpIntegrations(true);
        const result = await mcpService.listManagedIntegrations(agentInstanceId);
        // List every integration enabled on the agent type (same as API `items`). Do not filter to
        // `connection?.isEnabled` — that hid servers (e.g. Calendly) that are on the type but not yet
        // connected or flagged enabled on the connection row.
        setMcpIntegrations(result.items || []);
      } catch (err: any) {
        console.error('Failed to load MCP integrations:', err);
      } finally {
        setLoadingMcpIntegrations(false);
      }
    };
    load();
  }, [isOpen, agentInstanceId]);

  useEffect(() => {
    if (!isOpen || !agentInstanceId || !mcpServerId || bindingType !== 'mcp_tool') {
      setMcpTools([]);
      return;
    }
    const loadTools = async () => {
      try {
        setLoadingMcpTools(true);
        const result = await mcpService.listManagedIntegrationTools(agentInstanceId, mcpServerId);
        setMcpTools(result.items || []);
      } catch (err: any) {
        console.error('Failed to load MCP tools:', err);
        setMcpTools([]);
      } finally {
        setLoadingMcpTools(false);
      }
    };
    loadTools();
  }, [isOpen, agentInstanceId, mcpServerId, bindingType]);



  // useEffect(() => {
  //   if (isOpen) {
  //     if (initialConfig) {
  //       setName(initialConfig.name || '');
  //       setDescription(initialConfig.description || '');
  //       setUrl(initialConfig.url || '');
  //       setMethod(initialConfig.method || 'POST');
  //       setHeaders(initialConfig.headers || []);
  //       setQueryParams(initialConfig.queryParams || []);
  //       setBodyTemplate(initialConfig.bodyTemplate || []);
  //     } else {
  //       resetForm();
  //     }
  //   }
  // }, [isOpen, initialConfig]);

  const resetForm = () => {
    setBindingType('api_endpoint');
    setMcpServerId('');
    setMcpToolName('');
    setMcpInputRows([]);
    setName('');
    setDescription('');
    setUrl('');
    setMethod('POST');
    setHeaders([]);
    setQueryParams([]);
    setBodyTemplate([]);
    setBodyTemplateWithMetaData([]);
    setResponsePath('');
    setAuthMethod('POST');
    setAuthUrl('');
    setAuthHeaders([]);
    setAuthBody([]);
    setAuthTokenResponsePath('');
    setAuthTemplateVariable('');
    setAuthAttachScheme('none');
    setBasicAuthUsername('');
    setBasicAuthPassword('');
  };

  const updateRow = (
    idx: number,
    field: 'key' | 'value',
    value: string,
    rows: KeyValue[],
    setter: React.Dispatch<React.SetStateAction<KeyValue[]>>
  ) => {
    setter(rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const getRowSelectOptions = (row: KeyValue): SelectOption[] =>
    normalizeSelectOptions(row.meta?.options, true);

  const setRowSelectOptions = (
    setter: React.Dispatch<React.SetStateAction<KeyValue[]>>,
    rowIdx: number,
    updater: (currentOptions: SelectOption[]) => SelectOption[]
  ) => {
    setter(prev =>
      prev.map((r, i) => {
        if (i !== rowIdx) return r;
        const currentOptions = normalizeSelectOptions(r.meta?.options, true);
        return {
          ...r,
          meta: {
            ...(r.meta || {}),
            options: updater(currentOptions),
          },
        };
      })
    );
  };


  const updateFieldSourceActions = (
    setter: React.Dispatch<React.SetStateAction<KeyValue[]>>,
    fieldIdx: number,
    sourceActions: FieldSourceActionRef[]
  ) => {
    setter((prev) =>
      prev.map((r, i) =>
        i === fieldIdx
          ? { ...r, meta: { ...(r.meta || {}), sourceActions } }
          : r
      )
    );
  };

  const addFieldSourceAction = (
    setter: React.Dispatch<React.SetStateAction<KeyValue[]>>,
    rows: KeyValue[],
    fieldIdx: number
  ) => {
    const first = availableSourceActions[0]?.capabilityCode;
    if (!first) return;
    const current = rows[fieldIdx]?.meta?.sourceActions || [];
    updateFieldSourceActions(setter, fieldIdx, [
      ...current,
      { capabilityCode: first, responsePath: '' },
    ]);
  };

  const renderSourceActionsEditor = (
    setter: React.Dispatch<React.SetStateAction<KeyValue[]>>,
    rows: KeyValue[],
    idx: number,
    row: KeyValue,
    keyPrefix: string
  ) => (
    <div className="w-full basis-full mt-1 pl-0 md:pl-10">
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-xs font-medium text-agentic-text-secondary">
          Source actions (populate dynamic options / validate answer after user replies)
        </span>
        <Button tone="outline"
          type="button"
          disabled={availableSourceActions.length === 0}
          className="text-xs px-2 py-1 rounded-lg border disabled:opacity-40"
          onClick={() => addFieldSourceAction(setter, rows, idx)}
        >
          + Link action
        </Button>
      </div>
      {availableSourceActions.length === 0 && (
        <p className="text-xs text-agentic-text-secondary">
          Configure other actions on this agent first (e.g. meeting-details, available-dates).
        </p>
      )}
      {(row.meta?.sourceActions || []).map((ref, refIdx) => (
        <div
          key={`${keyPrefix}-${idx}-src-${refIdx}`}
          className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2"
        >
          <Button tone="outline-danger"
            type="button"
            className="self-start sm:self-auto px-2 py-1 rounded-lg border"
            aria-label="Remove linked action"
            onClick={() => {
              const next = (row.meta?.sourceActions || []).filter(
                (_, i) => i !== refIdx
              );
              updateFieldSourceActions(setter, idx, next);
            }}
          >
            ✕
          </Button>
          <SelectField tone="default-focus-primary"
            value={ref.capabilityCode}
            onChange={(e) => {
              const next = [...(row.meta?.sourceActions || [])];
              next[refIdx] = { ...next[refIdx], capabilityCode: e.target.value };
              updateFieldSourceActions(setter, idx, next);
            }}
            className="flex-1 min-w-0 px-3 py-2 rounded-lg border text-sm focus:ring-2"
          >
            {availableSourceActions.map((a) => (
              <option key={a.capabilityCode} value={a.capabilityCode}>
                {a.name || a.capabilityCode}
              </option>
            ))}
          </SelectField>
          <TextField tone="default-focus-primary"
            value={ref.responsePath || ''}
            onChange={(e) => {
              const next = [...(row.meta?.sourceActions || [])];
              next[refIdx] = { ...next[refIdx], responsePath: e.target.value };
              updateFieldSourceActions(setter, idx, next);
            }}
            placeholder="Response path (e.g. data)"
            className="flex-1 min-w-0 px-3 py-2 rounded-lg border text-sm focus:ring-2"
          />
        </div>
      ))}
      {(row.meta?.sourceActions?.length ?? 0) > 0 && (
        <p className="text-xs text-agentic-text-secondary">
          Actions run in order after the user answers; the LLM builds each request body and checks the response. Response path helps validation focus on the right JSON (e.g.{' '}
          <code className="text-[11px]">data</code>).
        </p>
      )}
    </div>
  );

  const handleTest = async () => {
    setTesting(true);
    try {
      const cfg: ActionConfig = {
        name,
        url,
        method,
        headers,
        queryParams,
        description,
        bodyTemplate,
      };

      if (onTest) {
        await onTest(cfg);
      } else {
        const hdrs = headers.reduce<Record<string, string>>((acc, r) => {
          if (r.key) acc[r.key] = r.value ?? '';
          return acc;
        }, {});

        const params = queryParams.reduce<Record<string, string>>((acc, r) => {
          if (r.key) acc[r.key] = r.value ?? '';
          return acc;
        }, {});

        const requestUrl = new URL(url);
        Object.entries(params).forEach(([k, v]) => {
          requestUrl.searchParams.set(k, v);
        });

        const requestBody =
          method === 'GET'
            ? undefined
            : bodyTemplate.reduce<Record<string, string>>((acc, r) => {
              if (r.key) acc[r.key] = r.value ?? '';
              return acc;
            }, {});

        const resp = await fetch(requestUrl.toString(), {
          method,
          headers: { 'Content-Type': 'application/json', ...hdrs },
          body: JSON.stringify(requestBody),
        });

        toast.success(`Test completed: ${resp.status} ${resp.statusText}`);
      }
    } catch (e) {
      toast.error('Test failed. Check console for details.');
      console.error('ActionConfigModal Test error:', e);
    } finally {
      setTesting(false);
    }
  };

  const handleSubmit = () => {

    console.log("bodytemplate==>", bodyTemplate)

    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (bindingType === 'api_endpoint' && !url.trim()) {
      toast.error('API URL is required');
      return;
    }
    if (bindingType === 'mcp_tool' && (!mcpServerId || !mcpToolName)) {
      toast.error('Select connected integration and MCP tool');
      return;
    }

    if (authAttachScheme === 'bearer') {
      if (!authUrl.trim()) {
        toast.error('Bearer: token API URL is required');
        return;
      }
      if (!authTokenResponsePath.trim()) {
        toast.error('Bearer: token response path is required (e.g. access_token)');
        return;
      }
    }

    const requiredParams =
      capabilityDetails?.config?.action?.parameters?.filter((p: any) => p.required) || [];

    for (const p of requiredParams) {
      const row = bodyTemplateWithMetaData.find(r => r.key === p.name);
      if (!row || !row.value?.trim()) {
        toast.error(`Required field "${p.name}" is missing`);
        return;
      }
    }

    const sanitizeRowOptions = (rows: KeyValue[]): KeyValue[] =>
      rows.map(row => ({
        ...row,
        meta: row.meta
          ? {
              ...row.meta,
              options: normalizeSelectOptions(row.meta.options),
            }
          : row.meta,
      }));

    const sanitizedBodyTemplateWithMetaData = sanitizeRowOptions(bodyTemplateWithMetaData);
    const sanitizedQueryParams = sanitizeRowOptions(queryParams);

    const queryMetaForConversational: KeyValue[] = sanitizedQueryParams
      .filter(r => r.key.trim() && typeof r.value === 'string' && r.value.includes('{{'))
      .map(r => ({
        key: r.key,
        value: r.value,
        meta: r.meta,
      }));

    const authFetchPayload =
      authAttachScheme === 'bearer' && authUrl.trim() && authTokenResponsePath.trim()
        ? {
            enabled: true,
            method: authMethod,
            url: authUrl.trim(),
            headers: Object.fromEntries(
              authHeaders.filter(r => r.key.trim()).map(r => [r.key.trim(), r.value ?? ''])
            ),
            body: Object.fromEntries(
              authBody.filter(r => r.key.trim()).map(r => [r.key.trim(), r.value ?? ''])
            ),
            tokenResponsePath: authTokenResponsePath.trim(),
            ...(authTemplateVariable.trim()
              ? { templateVariable: authTemplateVariable.trim() }
              : {}),
          }
        : undefined;

    const authAttachPayload =
      authAttachScheme !== 'none'
        ? {
            scheme: authAttachScheme,
            ...(authAttachScheme === 'basic'
              ? {
                  basicUsername: basicAuthUsername,
                  basicPassword: basicAuthPassword,
                }
              : {}),
          }
        : undefined;

    const cfg: ActionConfig = {
      bindingType,
      mcpServerId: bindingType === 'mcp_tool' ? mcpServerId : undefined,
      mcpToolName: bindingType === 'mcp_tool' ? mcpToolName : undefined,
      mcpInputTemplate:
        bindingType === 'mcp_tool'
          ? (() => {
              const filtered = mcpInputRows.filter((r) => r.key.trim());
              const obj: Record<string, any> = {};
              for (const r of filtered) obj[r.key.trim()] = r.value ?? '';
              obj.__order = filtered.map((r) => r.key.trim());
              const types: Record<string, string> = {};
              const options: Record<string, Array<{label: string; value: string}>> = {};
              const formats: Record<string, string> = {};
              const sourceActions: Record<string, FieldSourceActionRef[]> = {};
              const DATETIME_VALUE_TYPES = new Set(['date', 'time', 'datetime']);
              for (const r of filtered) {
                if (r.meta?.validationType) types[r.key.trim()] = r.meta.validationType;
                if (r.meta?.validationType === 'enum' && Array.isArray(r.meta?.options) && r.meta.options.length > 0) {
                  options[r.key.trim()] = r.meta.options as Array<{label: string; value: string}>;
                }
                if (
                  r.meta?.validationType &&
                  DATETIME_VALUE_TYPES.has(r.meta.validationType) &&
                  r.meta?.format?.trim()
                ) {
                  formats[r.key.trim()] = r.meta.format.trim();
                }
                if (
                  r.meta?.validationType === 'enum' &&
                  Array.isArray(r.meta?.sourceActions) &&
                  r.meta.sourceActions.length > 0
                ) {
                  sourceActions[r.key.trim()] = r.meta.sourceActions;
                }
              }
              if (Object.keys(types).length) obj.__types = types;
              if (Object.keys(options).length) obj.__options = options;
              if (Object.keys(formats).length) obj.__formats = formats;
              if (Object.keys(sourceActions).length) obj.__sourceActions = sourceActions;
              return obj;
            })()
          : undefined,
      name,
      url,
      method,
      headers: headers.filter(r => r.key.trim()),
      queryParams: sanitizedQueryParams.filter(r => r.key.trim()),
      description,
      bodyTemplateWithMetaData: {
        body: sanitizedBodyTemplateWithMetaData.filter(r => r.key.trim()),
        ...(queryMetaForConversational.length ? { query: queryMetaForConversational } : {}),
        ...(responsePath.trim() ? { responsePath: responsePath.trim() } : {}),
        ...(authFetchPayload ? { authFetch: authFetchPayload } : {}),
        ...(authAttachPayload ? { authAttach: authAttachPayload } : {}),
      },
      bodyTemplate: bodyTemplate.filter(r => r.key.trim()),

    };

    onSubmit(cfg);
    resetForm();
  };


  if (!isOpen) return null;

  const modalContent = (
    <Overlay className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <Surface tone="popover" className="relative w-full max-w-4xl mx-4 rounded-2xl border p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-agentic-text-primary">
            Configure Action{capabilityCode && `: ${capabilityCode}`}
          </h3>
          <Button tone="custom"
            className="p-1.5 rounded-lg hover:bg-gray-100"
            onClick={onClose}
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </Button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-agentic-text-primary mb-1">
              Binding Type
            </label>
            <SelectField tone="default-focus-primary"
              value={bindingType}
              onChange={(e) => setBindingType(e.target.value as 'api_endpoint' | 'mcp_tool')}
              className="w-full md:max-w-sm px-3 py-2 rounded-lg border focus:ring-2"
            >
              <option value="api_endpoint">API Endpoint</option>
              <option value="mcp_tool">MCP Tool</option>
            </SelectField>
          </div>

          {bindingType === 'mcp_tool' && (
            <div className="border rounded-xl p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-agentic-text-primary mb-1">
                    Connected Integration
                  </label>
                  <SelectField tone="default-focus-primary"
                    value={mcpServerId}
                    onChange={(e) => {
                      setMcpServerId(e.target.value);
                      setMcpToolName('');
                    }}
                    className="w-full px-3 py-2 rounded-lg border focus:ring-2"
                  >
                    <option value="">
                      {loadingMcpIntegrations ? 'Loading...' : 'Select integration'}
                    </option>
                    {mcpIntegrations.map((integration) => (
                      <option key={integration.id} value={integration.id}>
                        {integration.name}
                      </option>
                    ))}
                  </SelectField>
                </div>

                <div>
                  <label className="block text-sm font-medium text-agentic-text-primary mb-1">
                    MCP Tool
                  </label>
                  <SelectField tone="default-focus-primary"
                    value={mcpToolName}
                    onChange={(e) => setMcpToolName(e.target.value)}
                    disabled={!mcpServerId || loadingMcpTools}
                    className="w-full px-3 py-2 rounded-lg border focus:ring-2 disabled:opacity-60"
                  >
                    <option value="">
                      {loadingMcpTools ? 'Loading tools...' : 'Select tool'}
                    </option>
                    {mcpTools.map((tool) => (
                      <option key={tool.id} value={tool.toolName}>
                        {tool.toolName}
                      </option>
                    ))}
                  </SelectField>
                </div>
              </div>

              <div className="border rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-medium text-agentic-text-primary">MCP Input Template</div>
                  <Button tone="outline"
                    type="button"
                    className="text-sm px-3 py-1.5 rounded-lg border"
                    onClick={() => setMcpInputRows((rows) => [...rows, { key: '', value: '', meta: { validationType: '' } }])}
                  >
                    + Add Field
                  </Button>
                </div>
                <div className="space-y-2">
                  {mcpInputRows.map((row, idx) => (
                    <div
                      key={idx}
                      draggable
                      onDragStart={() => { dragIndexRef.current = idx; }}
                      onDragOver={(e) => { e.preventDefault(); }}
                      onDrop={() => {
                        const from = dragIndexRef.current;
                        if (from === null || from === idx) return;
                        setMcpInputRows((rows) => {
                          const next = [...rows];
                          const [moved] = next.splice(from, 1);
                          next.splice(idx, 0, moved);
                          return next;
                        });
                        dragIndexRef.current = null;
                      }}
                      onDragEnd={() => { dragIndexRef.current = null; }}
                      className="flex flex-col gap-2 cursor-grab active:opacity-50"
                    >
                      {/* Main field row */}
                      <div className="flex flex-col md:flex-row md:items-center gap-2">
                        <span className="hidden md:flex items-center text-gray-300 select-none px-1">⠿</span>
                        <Button tone="outline-danger"
                          type="button"
                          className="self-start md:self-auto px-2 py-1 rounded-lg border"
                          onClick={() => setMcpInputRows((rows) => rows.filter((_, i) => i !== idx))}
                        >
                          ✕
                        </Button>
                        <TextField tone="default-focus-primary"
                          value={row.key}
                          onChange={(e) => updateRow(idx, 'key', e.target.value, mcpInputRows, setMcpInputRows)}
                          placeholder="key (e.g. start_time)"
                          className="flex-1 px-3 py-2 rounded-lg border focus:ring-2"
                        />
                        <TextField tone="default-focus-primary"
                          value={row.value}
                          onChange={(e) => updateRow(idx, 'value', e.target.value, mcpInputRows, setMcpInputRows)}
                          placeholder="{{variable}} or static value"
                          className="flex-1 px-3 py-2 rounded-lg border focus:ring-2"
                        />
                        <SelectField
                          value={row.meta?.validationType || ''}
                          onChange={(e) => setMcpInputRows((rows) => rows.map((r, i) =>
                            i === idx ? { ...r, meta: { ...r.meta, validationType: e.target.value } } : r
                          ))}
                          className="px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent text-sm bg-white"
                        >
                          <option value="">— type —</option>
                          <option value="text">Text</option>
                          <option value="name">Name</option>
                          <option value="email">Email</option>
                          <option value="phone">Phone</option>
                          <option value="number">Number</option>
                          <option value="integer">Integer</option>
                          <option value="boolean">Yes / No</option>
                          <option value="url">URL</option>
                          <option value="zip">ZIP / Postal</option>
                          <option value="currency">Currency</option>
                          <option value="address">Address</option>
                          <option value="date">Date</option>
                          <option value="time">Time</option>
                          <option value="datetime">Date &amp; Time</option>
                          <option value="duration">Duration</option>
                          <option value="dob">Date of Birth</option>
                          <option value="enum">Enum</option>
                        </SelectField>
                      </div>

                      {/* Date/time/datetime submission format */}
                      {(row.meta?.validationType === 'date' ||
                        row.meta?.validationType === 'time' ||
                        row.meta?.validationType === 'datetime') && (() => {
                        const presets =
                          row.meta?.validationType === 'date' ? DATE_FORMATS
                          : row.meta?.validationType === 'time' ? TIME_FORMATS
                          : DATETIME_FORMATS;
                        const currentFormat = row.meta?.format || '';
                        const showCustomInput =
                          !!row.meta?.formatCustom ||
                          (!!currentFormat && !presets.some((f) => f.value === currentFormat));
                        return (
                          <div className="ml-8 flex flex-col md:flex-row gap-2 border-l-2 border-gray-100 pl-4">
                            <SelectField
                              value={showCustomInput ? '__custom__' : currentFormat}
                              onChange={(e) => {
                                const val = e.target.value;
                                setMcpInputRows((rows) => rows.map((r, i) => {
                                  if (i !== idx) return r;
                                  if (val === '__custom__') {
                                    return { ...r, meta: { ...r.meta, formatCustom: true } };
                                  }
                                  return { ...r, meta: { ...r.meta, format: val, formatCustom: false } };
                                }));
                              }}
                              className="w-full md:w-auto md:max-w-[260px] px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-agentic-primary-blue focus:border-transparent text-sm bg-white"
                            >
                              <option value="">Ask naturally — submit in default format</option>
                              {presets.map((f) => (
                                <option key={f.value} value={f.value}>{f.label}</option>
                              ))}
                              <option value="__custom__">Custom format…</option>
                            </SelectField>
                            {showCustomInput && (
                              <TextField tone="default-focus-primary"
                                value={currentFormat}
                                onChange={(e) =>
                                  setMcpInputRows((rows) => rows.map((r, i) =>
                                    i === idx
                                      ? { ...r, meta: { ...r.meta, format: e.target.value, formatCustom: true } }
                                      : r
                                  ))
                                }
                                placeholder="Custom format, e.g. YYYY-MM-DD[T]HH:mm:ssZ"
                                className="flex-1 px-3 py-2 rounded-lg border focus:ring-2"
                              />
                            )}
                          </div>
                        );
                      })()}

                      {/* Enum options sub-rows */}
                      {row.meta?.validationType === 'enum' && (
                        <div className="ml-8 space-y-2 border-l-2 border-gray-100 pl-4">
                          {(row.meta?.options as Array<{label: string; value: string}> || []).map((opt: {label: string; value: string}, optIdx: number) => (
                            <div key={optIdx} className="flex flex-col md:flex-row gap-2">
                              <TextField tone="default-focus-primary"
                                placeholder="Option label (shown to user)"
                                value={opt.label}
                                onChange={(e) => setMcpInputRows(prev => prev.map((r, i) => {
                                  if (i !== idx) return r;
                                  const opts = [...(r.meta?.options as Array<{label: string; value: string}> || [])];
                                  opts[optIdx] = { ...opts[optIdx], label: e.target.value };
                                  return { ...r, meta: { ...r.meta, options: opts } };
                                }))}
                                className="flex-1 px-3 py-2 rounded-lg border focus:ring-2"
                              />
                              <TextField tone="default-focus-primary"
                                placeholder="Option value (sent to API)"
                                value={opt.value}
                                onChange={(e) => setMcpInputRows(prev => prev.map((r, i) => {
                                  if (i !== idx) return r;
                                  const opts = [...(r.meta?.options as Array<{label: string; value: string}> || [])];
                                  opts[optIdx] = { ...opts[optIdx], value: e.target.value };
                                  return { ...r, meta: { ...r.meta, options: opts } };
                                }))}
                                className="flex-1 px-3 py-2 rounded-lg border focus:ring-2"
                              />
                              <Button tone="outline-danger"
                                type="button"
                                className="px-2 py-1 rounded-lg border"
                                onClick={() => setMcpInputRows(prev => prev.map((r, i) => {
                                  if (i !== idx) return r;
                                  const opts = (r.meta?.options as Array<{label: string; value: string}> || []).filter((_: any, j: number) => j !== optIdx);
                                  return { ...r, meta: { ...r.meta, options: opts } };
                                }))}
                              >
                                ✕
                              </Button>
                            </div>
                          ))}
                          <Button tone="outline"
                            type="button"
                            className="text-sm px-3 py-1.5 rounded-lg border"
                            onClick={() => setMcpInputRows(prev => prev.map((r, i) => {
                              if (i !== idx) return r;
                              const opts = [...(r.meta?.options as Array<{label: string; value: string}> || []), { label: '', value: '' }];
                              return { ...r, meta: { ...r.meta, options: opts } };
                            }))}
                          >
                            + Add Option
                          </Button>
                        </div>
                      )}

                      {row.meta?.validationType === 'enum' &&
                        renderSourceActionsEditor(setMcpInputRows, mcpInputRows, idx, row, 'mcp')}
                    </div>
                  ))}
                  {mcpInputRows.length === 0 && (
                    <div className="text-sm text-agentic-text-secondary">
                      No template fields. Tool input will be built from runtime data only.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-agentic-text-primary mb-1">
                Name
              </label>
              <TextField tone="default-focus-primary"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Action name"
                className="w-full px-3 py-2 rounded-lg border focus:ring-2"
              />
            </div>

            {bindingType === 'api_endpoint' && (
              <div>
                <label className="block text-sm font-medium text-agentic-text-primary mb-1">
                  Method
                </label>
                <SelectField tone="default-focus-primary"
                  value={method}
                  onChange={(e) => setMethod(e.target.value as HttpMethod)}
                  className="w-full px-3 py-2 rounded-lg border focus:ring-2"
                >
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="PATCH">PATCH</option>
                  <option value="DELETE">DELETE</option>
                </SelectField>
              </div>
            )}
          </div>

          {bindingType === 'api_endpoint' && (
            <div>
              <label className="block text-sm font-medium text-agentic-text-primary mb-1">
                API URL
              </label>
              <TextField tone="default-focus-primary"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://api.example.com/endpoint"
                className="w-full px-3 py-2 rounded-lg border focus:ring-2"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-agentic-text-primary mb-1">
              Description (Optional)
            </label>
            <TextArea tone="default-focus-primary"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe this action..."
              rows={2}
              className="w-full px-3 py-2 rounded-lg border focus:ring-2"
            />
          </div>

          {/* Response Path */}
          <div>
            <label className="block text-sm font-medium text-agentic-text-primary mb-1">
              Response Path (Optional)
            </label>
            <TextField tone="default-focus-primary"
              value={responsePath}
              onChange={(e) => setResponsePath(e.target.value)}
              placeholder="e.g. lbl.data or choices.0.text"
              className="w-full px-3 py-2 rounded-lg border focus:ring-2"
            />
            <p className="mt-1 text-xs text-agentic-text-secondary">
              Path inside the JSON response to use as the chat reply. Leave empty to use the full response.
            </p>
          </div>

          {/* Main API: how to send Authorization — selected first; determines which auth config
              shows below (headers editor / basic fields / prefetch config). Available for both
              api_endpoint and mcp_tool bindings, since different MCP tools on the same server can
              need different auth (e.g. exchanging a scoped edular-core ApiKeys-module key for a
              calendar-api token before the real call). */}
          <div className="border rounded-xl p-4 space-y-3">
            <div className="font-medium text-agentic-text-primary">
              Main API — authorization type
            </div>
            <p className="text-xs text-agentic-text-secondary">
              Applied on the server on each call. None lets you set headers manually. Bearer fetches a token from
              an auth API first, then sends it as Authorization. Basic encodes a username/password as HTTP Basic.
            </p>
            <SelectField tone="default-focus-primary"
              value={authAttachScheme}
              onChange={e =>
                setAuthAttachScheme(e.target.value as 'none' | 'bearer' | 'basic')
              }
              className="w-full max-w-md px-3 py-2 rounded-lg border focus:ring-2"
            >
              <option value="none">None (manual headers)</option>
              <option value="bearer">Bearer (token from prefetch)</option>
              <option value="basic">Basic (username / password)</option>
            </SelectField>

            {authAttachScheme === 'bearer' && (
              <div className="space-y-3 border-t border-gray-100 pt-3">
                <p className="text-xs text-agentic-text-secondary">
                  Runs on the server before each call to fetch a token. Use the same dot notation as response path
                  (e.g. <code className="text-[11px]">access_token</code>). The{' '}
                  <code className="text-[11px]">Authorization</code> header is set to{' '}
                  <code className="text-[11px]">Bearer &lt;token&gt;</code> automatically — no need to add it in
                  Header Fields.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-agentic-text-primary mb-1">
                      Token request method
                    </label>
                    <SelectField tone="default-focus-primary"
                      value={authMethod}
                      onChange={e => setAuthMethod(e.target.value as HttpMethod)}
                      className="w-full px-3 py-2 rounded-lg border focus:ring-2"
                    >
                      <option value="GET">GET</option>
                      <option value="POST">POST</option>
                      <option value="PUT">PUT</option>
                      <option value="PATCH">PATCH</option>
                    </SelectField>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-agentic-text-primary mb-1">
                      Token API URL
                    </label>
                    <TextField tone="default-focus-primary"
                      value={authUrl}
                      onChange={e => setAuthUrl(e.target.value)}
                      placeholder="https://api.example.com/Token/GetToken"
                      className="w-full px-3 py-2 rounded-lg border focus:ring-2"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-agentic-text-primary mb-1">
                      Token response path
                    </label>
                    <TextField tone="default-focus-primary"
                      value={authTokenResponsePath}
                      onChange={e => setAuthTokenResponsePath(e.target.value)}
                      placeholder="access_token"
                      className="w-full px-3 py-2 rounded-lg border focus:ring-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-agentic-text-primary mb-1">
                      Template variable (optional)
                    </label>
                    <TextField tone="default-focus-primary"
                      value={authTemplateVariable}
                      onChange={e => setAuthTemplateVariable(e.target.value)}
                      placeholder="Defaults to last segment of path"
                      className="w-full px-3 py-2 rounded-lg border focus:ring-2"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-medium text-agentic-text-primary text-sm">
                      Token request headers
                    </div>
                    <Button tone="outline"
                      type="button"
                      className="text-sm px-3 py-1.5 rounded-lg border"
                      onClick={() => setAuthHeaders(rows => [...rows, { key: '', value: '' }])}
                    >
                      + Add Header
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {authHeaders.map((row, idx) => (
                      <div key={idx} className="flex flex-col md:flex-row md:items-center gap-2">
                        <Button tone="outline-danger"
                          type="button"
                          className="self-start md:self-auto px-2 py-1 rounded-lg border"
                          aria-label="Remove row"
                          onClick={() => setAuthHeaders(rows => rows.filter((_, i) => i !== idx))}
                        >
                          ✕
                        </Button>
                        <TextField tone="default-focus-primary"
                          value={row.key}
                          onChange={e =>
                            updateRow(idx, 'key', e.target.value, authHeaders, setAuthHeaders)
                          }
                          placeholder="Content-Type"
                          className="flex-1 px-3 py-2 rounded-lg border focus:ring-2"
                        />
                        <TextField tone="default-focus-primary"
                          value={row.value}
                          onChange={e =>
                            updateRow(idx, 'value', e.target.value, authHeaders, setAuthHeaders)
                          }
                          placeholder="application/json"
                          className="flex-1 px-3 py-2 rounded-lg border focus:ring-2"
                        />
                      </div>
                    ))}
                    {authHeaders.length === 0 && (
                      <div className="text-sm text-agentic-text-secondary">No headers (defaults include JSON).</div>
                    )}
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-medium text-agentic-text-primary text-sm">
                      Token request body (JSON fields)
                    </div>
                    <Button tone="outline"
                      type="button"
                      className="text-sm px-3 py-1.5 rounded-lg border"
                      onClick={() => setAuthBody(rows => [...rows, { key: '', value: '' }])}
                    >
                      + Add Field
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {authBody.map((row, idx) => (
                      <div key={idx} className="flex flex-col md:flex-row md:items-center gap-2">
                        <Button tone="outline-danger"
                          type="button"
                          className="self-start md:self-auto px-2 py-1 rounded-lg border"
                          aria-label="Remove row"
                          onClick={() => setAuthBody(rows => rows.filter((_, i) => i !== idx))}
                        >
                          ✕
                        </Button>
                        <TextField tone="default-focus-primary"
                          value={row.key}
                          onChange={e => updateRow(idx, 'key', e.target.value, authBody, setAuthBody)}
                          placeholder="userName"
                          className="flex-1 px-3 py-2 rounded-lg border focus:ring-2"
                        />
                        <TextField tone="default-focus-primary"
                          value={row.value}
                          onChange={e => updateRow(idx, 'value', e.target.value, authBody, setAuthBody)}
                          placeholder="static value or JSON"
                          className="flex-1 px-3 py-2 rounded-lg border focus:ring-2"
                        />
                      </div>
                    ))}
                    {authBody.length === 0 && (
                      <div className="text-sm text-agentic-text-secondary">
                        Empty body for GET; for POST add credentials as key/value pairs.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {authAttachScheme === 'basic' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-agentic-text-primary mb-1">
                    Basic username
                  </label>
                  <TextField tone="default-focus-primary"
                    value={basicAuthUsername}
                    onChange={e => setBasicAuthUsername(e.target.value)}
                    placeholder="user or {{fieldFromConversation}}"
                    autoComplete="off"
                    className="w-full px-3 py-2 rounded-lg border focus:ring-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-agentic-text-primary mb-1">
                    Basic password
                  </label>
                  <TextField tone="default-focus-primary"
                    type="password"
                    value={basicAuthPassword}
                    onChange={e => setBasicAuthPassword(e.target.value)}
                    placeholder="password or {{field}}"
                    autoComplete="new-password"
                    className="w-full px-3 py-2 rounded-lg border focus:ring-2"
                  />
                </div>
                <p className="md:col-span-2 text-xs text-agentic-text-secondary">
                  Supports <code className="text-[11px]">{`{{name}}`}</code> placeholders using conversational fields
                  and tenant/client ids.
                </p>
              </div>
            )}
          </div>

          {/* Headers — manual header config only makes sense when auth type is None; Bearer/Basic
              set Authorization automatically and don't need a manual row for it. */}
          {authAttachScheme === 'none' && (
          <div className="border rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="font-medium text-agentic-text-primary">Header Fields</div>
              <Button tone="outline"
                type="button"
                className="text-sm px-3 py-1.5 rounded-lg border"
                onClick={() => setHeaders((r) => [...r, { key: '', value: '' }])}
              >
                + Add Header
              </Button>
            </div>
            <div className="space-y-2">
              {headers.map((row, idx) => (
                <div key={idx} className="flex flex-col md:flex-row md:items-center gap-2">
                  <Button tone="outline-danger"
                    type="button"
                    className="self-start md:self-auto px-2 py-1 rounded-lg border"
                    aria-label="Remove row"
                    onClick={() => setHeaders((rows) => rows.filter((_, i) => i !== idx))}
                  >
                    ✕
                  </Button>
                  <TextField tone="default-focus-primary"
                    value={row.key}
                    onChange={(e) => updateRow(idx, 'key', e.target.value, headers, setHeaders)}
                    placeholder="Key (e.g., Authorization)"
                    className="flex-1 px-3 py-2 rounded-lg border focus:ring-2"
                  />
                  <TextField tone="default-focus-primary"
                    value={row.value}
                    onChange={(e) =>
                      updateRow(idx, 'value', e.target.value, headers, setHeaders)
                    }
                    placeholder="Value"
                    className="flex-1 px-3 py-2 rounded-lg border focus:ring-2"
                  />
                </div>
              ))}
              {headers.length === 0 && (
                <div className="text-sm text-agentic-text-secondary">No headers added.</div>
              )}
              <p className="text-xs text-agentic-text-secondary mt-2">
                💡 Use template expressions like {`{{variable}}`} or static values. Values with template expressions stay as strings, others are JSON-parsed.
              </p>
            </div>
          </div>
          )}

          {/* Query Parameters */}
          <div className="border rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="font-medium text-agentic-text-primary">Query Parameters</div>
              <Button tone="outline"
                type="button"
                className="text-sm px-3 py-1.5 rounded-lg border"
                onClick={() => setQueryParams((r) => [...r, { key: '', value: '' }])}
              >
                + Add Param
              </Button>
            </div>
            <div className="space-y-2">
              {queryParams.map((row, idx) => (
                <div key={idx} className="flex flex-col gap-2 border-b border-gray-200 pb-3 mb-3">
                  <div className="flex flex-col md:flex-row md:items-start gap-2">
                    <Button tone="outline-danger"
                      type="button"
                      className="self-start md:self-auto px-2 py-1 rounded-lg border"
                      aria-label="Remove row"
                      onClick={() => setQueryParams((rows) => rows.filter((_, i) => i !== idx))}
                    >
                      ✕
                    </Button>
                    <TextField tone="default-focus-primary"
                      value={row.key}
                      onChange={(e) =>
                        updateRow(idx, 'key', e.target.value, queryParams, setQueryParams)
                      }
                      placeholder="Key"
                      className="flex-1 px-3 py-2 rounded-lg border focus:ring-2"
                    />
                    <TextField tone="default-focus-primary"
                      value={row.value}
                      onChange={(e) =>
                        updateRow(idx, 'value', e.target.value, queryParams, setQueryParams)
                      }
                      placeholder="Value or {{variable}}"
                      className="flex-1 px-3 py-2 rounded-lg border focus:ring-2"
                    />
                  </div>

                  {/* Query param value type + options */}
                  <div className="flex flex-col md:flex-row md:items-center gap-2">
                    <SelectField tone="default-focus-primary"
                      value={row.meta?.valueType || 'text'}
                      onChange={(e) =>
                        setQueryParams(prev =>
                          prev.map((r, i) =>
                            i === idx
                              ? {
                                  ...r,
                                  meta: {
                                    ...(r.meta || {}),
                                    valueType: e.target.value as FieldValueType,
                                  },
                                }
                              : r
                          )
                        )
                      }
                      className="w-full md:w-auto md:max-w-[180px] px-3 py-2 rounded-lg border focus:ring-2"
                    >
                      {VALUE_TYPE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </SelectField>

                    {(row.meta?.valueType === 'single-select' ||
                      row.meta?.valueType === 'multi-select') && (
                      <div className="w-full space-y-2">
                        {getRowSelectOptions(row).map((opt, optIdx) => (
                          <div key={optIdx} className="flex flex-col md:flex-row gap-2">
                            <TextField tone="default-focus-primary"
                              placeholder="Option label (shown to user)"
                              value={opt.label}
                              onChange={(e) => {
                                setRowSelectOptions(setQueryParams, idx, current =>
                                  current.map((x, i) =>
                                    i === optIdx ? { ...x, label: e.target.value } : x
                                  )
                                );
                              }}
                              className="flex-1 px-3 py-2 rounded-lg border focus:ring-2"
                            />
                            <TextField tone="default-focus-primary"
                              placeholder="Option value (sent to API)"
                              value={opt.value}
                              onChange={(e) => {
                                setRowSelectOptions(setQueryParams, idx, current =>
                                  current.map((x, i) =>
                                    i === optIdx ? { ...x, value: e.target.value } : x
                                  )
                                );
                              }}
                              className="flex-1 px-3 py-2 rounded-lg border focus:ring-2"
                            />
                            <Button tone="outline-danger"
                              type="button"
                              className="px-2 py-1 rounded-lg border"
                              onClick={() => {
                                setRowSelectOptions(setQueryParams, idx, current =>
                                  current.filter((_, i) => i !== optIdx)
                                );
                              }}
                            >
                              ✕
                            </Button>
                          </div>
                        ))}
                        <Button tone="outline"
                          type="button"
                          className="text-sm px-3 py-1.5 rounded-lg border"
                          onClick={() => {
                            setRowSelectOptions(setQueryParams, idx, current => [
                              ...current,
                              { label: '', value: '' },
                            ]);
                          }}
                        >
                          + Add Option
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {queryParams.length === 0 && (
                <div className="text-sm text-agentic-text-secondary">No parameters added.</div>
              )}
              <p className="text-xs text-agentic-text-secondary mt-2">
                💡 Use template expressions like {`{{variable}}`} or static values. Values with template expressions stay as strings, others are JSON-parsed.
              </p>
            </div>
          </div>

          {/* Body Template - Same as Headers */}
          {/* Body Template – Capability Driven */}
          {/* Body Template */}
          {method !== 'GET' && (
            <div className="border rounded-xl p-4 space-y-6">
              <div className="flex items-center justify-between mb-2">
                <div className="font-medium text-agentic-text-primary">
                  Request Body (Conversational Fields)
                </div>

                <Button tone="custom"
                  type="button"
                  className="text-sm px-3 py-1.5 rounded-lg border hover:bg-gray-50"
                  onClick={() =>
                    setBodyTemplateWithMetaData(prev => [
                      ...prev,
                      { key: '', value: '', meta: { valueType: 'text' } }
                    ])
                  }
                >
                  + Add Field
                </Button>
              </div>

              <div className="space-y-3">
                {bodyTemplateWithMetaData.map((row, idx) => (
                  <div key={idx} className="flex flex-col md:flex-row md:items-center gap-2 flex-wrap border-b border-gray-200 pb-4 mb-4">

                    <Button tone="outline-danger"
                      type="button"
                      className="self-start md:self-auto px-2 py-1 rounded-lg border"
                      aria-label="Remove row"
                      onClick={() =>
                        setBodyTemplateWithMetaData(prev => prev.filter((_, i) => i !== idx))
                      }
                    >
                      ✕
                    </Button>

                    {/* Key */}
                    <TextField tone="default-focus-primary"
                      value={row.key}
                      onChange={(e) =>
                        updateRow(idx, 'key', e.target.value, bodyTemplateWithMetaData, setBodyTemplateWithMetaData)
                      }
                      placeholder="Field key"
                      className="flex-1 px-3 py-2 rounded-lg border focus:ring-2"
                    />

                    {/* Template Value */}
                    <TextField tone="default-focus-primary"
                      value={row.value}
                      onChange={(e) =>
                        updateRow(idx, 'value', e.target.value, bodyTemplateWithMetaData, setBodyTemplateWithMetaData)
                      }
                      placeholder="{{variable}} or static"
                      className="flex-1 px-3 py-2 rounded-lg border focus:ring-2"
                    />

                    {/* Value Type */}
                    <SelectField tone="default-focus-primary"
                      value={row.meta?.valueType || 'text'}
                      onChange={(e) =>
                        setBodyTemplateWithMetaData(prev =>
                          prev.map((r, i) =>
                            i === idx
                              ? { ...r, meta: { ...(r.meta || {}), valueType: e.target.value as FieldValueType } }
                              : r
                          )
                        )
                      }
                      className="w-full md:w-auto md:max-w-[160px] px-3 py-2 rounded-lg border focus:ring-2"
                    >
                      {VALUE_TYPE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </SelectField>

                    {/* Format / Options */}
                    <div className="col-span-2">
                      {(row.meta?.valueType === 'date') && (
                        <SelectField tone="default-focus-primary"
                          value={row.meta?.format || ''}
                          onChange={(e) =>
                            setBodyTemplateWithMetaData(prev =>
                              prev.map((r, i) =>
                                i === idx ? { ...r, meta: { ...r.meta, format: e.target.value } } : r
                              )
                            )
                          }
                          className="w-full md:w-auto md:max-w-[220px] px-3 py-2 rounded-lg border focus:ring-2"
                        >
                          <option value="">Select date format</option>
                          {DATE_FORMATS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                        </SelectField>
                      )}

                      {(row.meta?.valueType === 'time') && (
                        <SelectField tone="default-focus-primary"
                          value={row.meta?.format || ''}
                          onChange={(e) =>
                            setBodyTemplateWithMetaData(prev =>
                              prev.map((r, i) =>
                                i === idx ? { ...r, meta: { ...r.meta, format: e.target.value } } : r
                              )
                            )
                          }
                          className="w-full md:w-auto md:max-w-[220px] px-3 py-2 rounded-lg border focus:ring-2"
                        >
                          <option value="">Select time format</option>
                          {TIME_FORMATS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                        </SelectField>
                      )}

                      {(row.meta?.valueType === 'datetime') && (
                        <SelectField tone="default-focus-primary"
                          value={row.meta?.format || ''}
                          onChange={(e) =>
                            setBodyTemplateWithMetaData(prev =>
                              prev.map((r, i) =>
                                i === idx ? { ...r, meta: { ...r.meta, format: e.target.value } } : r
                              )
                            )
                          }
                          className="w-full md:w-auto md:max-w-[220px] px-3 py-2 rounded-lg border focus:ring-2"
                        >
                          <option value="">Select datetime format</option>
                          {DATETIME_FORMATS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                        </SelectField>
                      )}

                      {(row.meta?.valueType === 'single-select' || row.meta?.valueType === 'multi-select' || row.meta?.valueType === 'enum') && (
                        <div className="w-full space-y-2">
                          {getRowSelectOptions(row).map((opt, optIdx) => (
                            <div key={optIdx} className="flex flex-col md:flex-row gap-2">
                              <TextField tone="default-focus-primary"
                                placeholder="Option label (shown to user)"
                                value={opt.label}
                                onChange={(e) =>
                                  setRowSelectOptions(setBodyTemplateWithMetaData, idx, current =>
                                    current.map((x, j) =>
                                      j === optIdx ? { ...x, label: e.target.value } : x
                                    )
                                  )
                                }
                                className="flex-1 px-3 py-2 rounded-lg border focus:ring-2"
                              />
                              <TextField tone="default-focus-primary"
                                placeholder="Option value (sent to API)"
                                value={opt.value}
                                onChange={(e) =>
                                  setRowSelectOptions(setBodyTemplateWithMetaData, idx, current =>
                                    current.map((x, j) =>
                                      j === optIdx ? { ...x, value: e.target.value } : x
                                    )
                                  )
                                }
                                className="flex-1 px-3 py-2 rounded-lg border focus:ring-2"
                              />
                              <Button tone="outline-danger"
                                type="button"
                                className="px-2 py-1 rounded-lg border"
                                onClick={() =>
                                  setRowSelectOptions(setBodyTemplateWithMetaData, idx, current =>
                                    current.filter((_, j) => j !== optIdx)
                                  )
                                }
                              >
                                ✕
                              </Button>
                            </div>
                          ))}
                          <Button tone="outline"
                            type="button"
                            className="text-sm px-3 py-1.5 rounded-lg border"
                            onClick={() =>
                              setRowSelectOptions(setBodyTemplateWithMetaData, idx, current => [
                                ...current,
                                { label: '', value: '' },
                              ])
                            }
                          >
                            + Add Option
                          </Button>
                        </div>
                      )}

                      {row.meta?.valueType === 'duration' && (
                        <SelectField tone="default-focus-primary"
                          value={row.meta?.format || ''}
                          onChange={(e) =>
                            setBodyTemplateWithMetaData(prev =>
                              prev.map((r, i) =>
                                i === idx ? { ...r, meta: { ...r.meta, format: e.target.value } } : r
                              )
                            )
                          }
                          className="w-full md:w-auto md:max-w-[220px] px-3 py-2 rounded-lg border focus:ring-2"
                        >
                          <option value="">Select duration format</option>
                          {DURATION_FORMATS.map(f => (
                            <option key={f.value} value={f.value}>{f.label}</option>
                          ))}
                        </SelectField>
                      )}

                    </div>

                    {renderSourceActionsEditor(setBodyTemplateWithMetaData, bodyTemplateWithMetaData, idx, row, 'body')}
                  </div>
                ))}

                {bodyTemplateWithMetaData.length === 0 && (
                  <div className="text-sm text-agentic-text-secondar">No conversational body fields configured.</div>
                )}
              </div>
            </div>
          )}

        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <Button tone="outline-muted"
            type="button"
            className="px-4 py-2 rounded-lg border disabled:opacity-50"
            onClick={handleTest}
            disabled={testing || bindingType === 'mcp_tool' || !url.trim()}
          >
            {testing ? 'Testing...' : 'Test Config'}
          </Button>
          <Button tone="outline-muted"
            type="button"
            className="px-4 py-2 rounded-lg border"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button tone="primary-fade"
            type="button"
            className="px-4 py-2 rounded-lg"
            onClick={handleSubmit}
          >
            Save Configuration
          </Button>
        </div>
      </Surface>
    </Overlay>
  );

  if (typeof document === 'undefined') return modalContent;
  return createPortal(modalContent, document.body);
};

export default ActionConfigModal;
